"use client";

import { defaultLocale, Locale, messages, TranslationKeys } from '@/language';
import { createContext, useContext, useState, useEffect, ReactNode, useCallback } from 'react';
import { usePathname, useSearchParams } from 'next/navigation';

interface LocaleContextType {
  locale: Locale;
  setLocale: (locale: Locale) => void;
  t: (key: TranslationKeys | string, params?: Record<string, string | number>) => string;
}

const LocaleContext = createContext<LocaleContextType | undefined>(undefined);

export function LocaleProvider({
  children,
  initialLocale = defaultLocale, 
}: {
  children: ReactNode;
  initialLocale?: Locale;
}) {
  const pathname = usePathname() || '';
  const searchParams = useSearchParams(); 
  

  const [locale, setLocaleState] = useState<Locale>(initialLocale);

  useEffect(() => {
    const langParam = searchParams?.get('lang');
    
    let localeFromUrl: Locale = 'id';

    if (langParam === 'en' || langParam === 'id') {
      localeFromUrl = langParam as Locale;
    } else {
      localeFromUrl = (pathname.startsWith('/en/') || pathname === '/en') ? 'en' : 'id';
    }

    if (localeFromUrl !== locale) {
      setLocaleState(localeFromUrl);
    }
  }, [pathname, searchParams, locale]); 

  useEffect(() => {
    document.documentElement.lang = locale;
  }, [locale]);

  const t = useCallback(
    (key: string | TranslationKeys, params?: Record<string, string | number>) => {
      const keys = (key as string).split('.');
      let value: string | Record<string, unknown> = messages[locale] || messages[defaultLocale];

      for (const k of keys) {
        if (typeof value === 'object' && value !== null && k in value) {
          value = value[k] as string | Record<string, unknown>;
        } else {
          return key as string;
        }
      }

      if (typeof value !== 'string') {
        return key as string;
      }

      if (params) {
        return Object.entries(params).reduce(
          (str, [paramKey, paramValue]) => str.replace(`{${paramKey}}`, String(paramValue)),
          value
        );
      }

      return value;
    },
    [locale]
  );

  return (
    <LocaleContext.Provider value={{ locale, setLocale: setLocaleState, t }}>
      {children}
    </LocaleContext.Provider>
  );
}

export function useLocale() {
  const context = useContext(LocaleContext);
  if (!context) {
    throw new Error('useLocale must be used within LocaleProvider');
  }
  return context;
}